<!-- eslint-disable import/order, perfectionist/sort-imports -->
<script setup lang="ts">
import { toTypedSchema } from '@vee-validate/zod';
import { useForm } from 'vee-validate';
import { computed, watch } from 'vue';

import type { InstallationMonorepo } from '~/services/installation-monorepo/installation-monorepo.types';

import ModalForm from '~/components/shared/ModalForm.vue';
import usePermission from '~/hooks/usePermission';

import useCreateInstallationBypass from '../queries/useCreateInstallationBypass';
import {
  INSTALLATION_BYPASS_STEP,
  type InstallationBypassForm,
  installationBypassSchema,
} from '../schemas/installationBypassSchema';

interface Props {
  initialRecord?: InstallationMonorepo.BypassRecord | null;
  mode?: 'create' | 'edit';
  modelValue: boolean;
  partnerId: number;
}

const props = withDefaults(defineProps<Props>(), {
  initialRecord: null,
  mode: 'create',
});

const emit = defineEmits<{
  close: [];
  'update:modelValue': [value: boolean];
}>();

const { hasAccess: canEdit } = usePermission(['installation_approvers:edit']);
const saveMutation = useCreateInstallationBypass();

const isVisible = computed({
  get: () => props.modelValue,
  set: (value: boolean) => emit('update:modelValue', value),
});

const isEditMode = computed(() => props.mode === 'edit');

const modalTitle = computed(() => (
  isEditMode.value ? 'Editar bypass de instalação' : 'Adicionar bypass de instalação'
));

const primaryButtonText = computed(() => (
  isEditMode.value ? 'Salvar' : 'Adicionar'
));

const isLoading = computed(() => saveMutation.isPending.value);

const emptyFormValues: InstallationBypassForm = {
  containers: false,
  reason: '',
  solfacil_orders: false,
};

const {
  defineField,
  errors,
  handleSubmit,
  resetForm,
} = useForm<InstallationBypassForm>({
  initialValues: emptyFormValues,
  validationSchema: toTypedSchema(installationBypassSchema),
});

const [reason, reasonProps] = defineField('reason');
const [separateInvoices, separateInvoicesProps] = defineField('solfacil_orders');
const [singleInvoices, singleInvoicesProps] = defineField('containers');

const characterCount = computed(() => reason.value?.length ?? 0);

function getFormValuesFromRecord(
  record: InstallationMonorepo.BypassRecord,
): InstallationBypassForm {
  return {
    containers: record.containers,
    reason: record.reason,
    solfacil_orders: record.solfacil_orders,
  };
}

const handleSave = handleSubmit(async (values) => {
  if (!canEdit.value) return;

  try {
    await saveMutation.mutateAsync({
      isUpdate: isEditMode.value,
      partnerId: props.partnerId,
      payload: {
        containers: values.containers,
        reason: values.reason,
        solfacil_orders: values.solfacil_orders,
        step_to_bypass: INSTALLATION_BYPASS_STEP,
      },
    });

    isVisible.value = false;
    emit('close');
  } catch {
    // handled by mutation
  }
});

function handleCancel() {
  if (!isLoading.value) {
    isVisible.value = false;
    emit('close');
  }
}

watch(isVisible, (open) => {
  if (!open) {
    resetForm({ values: emptyFormValues });
    return;
  }

  if (isEditMode.value && props.initialRecord) {
    resetForm({ values: getFormValuesFromRecord(props.initialRecord) });
    return;
  }

  resetForm({ values: emptyFormValues });
});
</script>

<template>
  <ModalForm
    v-model="isVisible"
    :title="modalTitle"
    mode="create"
    :loading="isLoading"
    :primary-button-text="primaryButtonText"
    size="md"
    @save="handleSave"
    @cancel="handleCancel"
  >
    <template #default="{ isLoading: modalIsLoading }">
      <div class="mb-3">
        <label class="form-label" for="bypass-step">
          Etapa
        </label>
        <input
          id="bypass-step"
          type="text"
          class="form-control"
          value="Confirmação do Cliente"
          disabled
          readonly
        />
      </div>

      <div class="mb-3">
        <label class="form-label required" for="bypass-reason">
          Motivo
        </label>
        <textarea
          id="bypass-reason"
          v-model="reason"
          v-bind="reasonProps"
          class="form-control"
          :class="{ 'is-invalid': errors.reason }"
          rows="4"
          maxlength="500"
          placeholder="Descreva o motivo do bypass de instalação"
          :disabled="modalIsLoading"
        />
        <div class="d-flex justify-content-between align-items-start gap-2 mt-1">
          <div v-if="errors.reason" class="invalid-feedback d-block mb-0">
            {{ errors.reason }}
          </div>
          <small class="text-muted ms-auto character-count">
            {{ characterCount }}/500 caracteres
          </small>
        </div>
      </div>

      <div class="mb-2">
        <div class="form-check">
          <input
            id="solfacil-orders"
            v-model="separateInvoices"
            v-bind="separateInvoicesProps"
            type="checkbox"
            class="form-check-input"
            :disabled="modalIsLoading"
          />
          <label class="form-check-label" for="solfacil-orders">
            Pedidos Solfácil
          </label>
        </div>
      </div>

      <div>
        <div class="form-check">
          <input
            id="containers"
            v-model="singleInvoices"
            v-bind="singleInvoicesProps"
            type="checkbox"
            class="form-check-input"
            :disabled="modalIsLoading"
          />
          <label class="form-check-label" for="containers">
            Container
          </label>
        </div>
      </div>
    </template>
  </ModalForm>
</template>

<style scoped lang="scss">
.form-label.required::after {
  content: ' *';
  color: var(--bs-danger);
}

.form-control {
  min-height: 100px;
  resize: vertical;
}

.character-count {
  flex: 0 0 auto;
  white-space: nowrap;
}
</style>
